import React from "react";
import styled from "styled-components";
import { Link } from "gatsby";
import colors from "../../styles/colors";
import fonts from "../../styles/fonts";
import { withTranslation } from "gatsby-plugin-react-i18next";
import BcorpCertified from "../../resources/img/footer/certified_b_corp.png";

const breakpoints = {
  md: "768px",
  xl: "1200px",
  xxl: "1440px",
};

const DesktopFooterContainer = styled.div`
  display: none;

  @media (min-width: ${breakpoints.md}) {
    display: block;
  }
`;

const FooterContainer = styled.div`
  background: ${colors.lightergreen};
  padding: 94px 0 30px;
`;

const FooterContent = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 80px; 
  max-width: 1200px; 
  margin: 0 auto; 

  @media (min-width: ${breakpoints.xl}) {
    gap: 140px;
  }
`;

const LogoMenusContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 40px;
`;

const Logo = styled.div`
  ${fonts.americana};
  font-size: 24px;
  color: ${colors.trueBlack};
`;

const Menus = styled.div`
  display: flex;
  gap: 80px;

  @media (min-width: ${breakpoints.xxl}) {
    gap: 80px;
  }
`;

const Column = styled.div`
  ${fonts.TTNormsPro};
  color: ${colors.trueBlack};
  font-size: 12px;
  line-height: 1.6;
  letter-spacing: 1px;
  display: grid;
  gap: 10px;

  a {
    color: inherit;
    text-decoration: none;
    text-transform: lowercase;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const SignupContainer = styled.div`
  min-width: 348px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  margin-left: auto;
  transform: translateX(-20%);
`;

const SignupHeading = styled.h4`
  ${fonts.americana};
  font-size: 16px;
  color: ${colors.trueBlack};
  margin-bottom: 8px;
`;

const Label = styled.label`
  ${fonts.TTNormsPro};
  font-size: 12px;
  color: ${colors.trueBlack};
  margin-bottom: 4px;
`;

const SignupInputContainer = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
  max-width: 348px;
`;

const SignupInput = styled.input`
  flex: 2;
  padding: 10px;
  border: 1px solid ${colors.trueBlack};
  font-family: ${fonts.TTNormsPro};
  font-size: 12px;

  ::placeholder {
    color: ${colors.mediumTextGrey};
    font-family: ${fonts.TTNormsPro};
    font-style: italic;
  }
`;

const BirthdayInput = styled(SignupInput)`
  flex: 1;
  max-width: 80px;
`;

const BirthdaySelect = styled.select`
  flex: 1;
  max-width: 80px;
  padding: 10px;
  border: 1px solid ${colors.trueBlack};
  font-family: ${fonts.TTNormsPro};
  font-size: 12px;

  ::placeholder {
    color: ${colors.mediumTextGrey};
    font-family: ${fonts.TTNormsPro};
    font-style: italic;
  }
`;

const SubscribeButton = styled.button`
  ${fonts.TTNormsPro};
  width: 100%;
  max-width: 348px;
  padding: 10px;
  background-color: ${colors.white};
  color: ${colors.trueBlack};
  border: 1px solid ${colors.trueBlack};
  font-size: 12px;
  text-transform: lowercase;
  cursor: pointer;
`;

const LocationContainer = styled.div`
  ${fonts.TTNormsPro};
  font-size: 10px;
  line-height: 1.6;
  color: ${colors.mediumTextGrey};
  background: ${colors.white};
  margin-top: 80px;
  padding: 7px 0;
`;

const LocationContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 12px;

  a {
    color: inherit;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const CertifiedImage = styled.img`
  width: 45px;
  height: 59px;
  position: absolute;
  bottom: 20px;
  right: 20px;
`;

const BFCMFooter = ({ handlePopup, t }) => {
  const socialLinks = [
    { name: "Instagram", link: "https://www.instagram.com/kimai_jewelry" },
    { name: "Facebook", link: "https://www.facebook.com/kimai.co" },
    { name: "Pinterest", link: "https://pin.it/5dPhxkW" },
  ];

  const menuList = [
    [
      { name: t("footer.link.faqs", "FAQs"), link: "/faq/" },
      { name: t("footer.link.shipping_returns", "Shipping & Returns"), link: "/faq/" },
      { name: t("footer.link.warranty", "Warranty"), link: "/faq/" },
      { name: t("footer.link.contact", "Contact Us"), link: "/contact/" },
    ],
    [
      { name: t("footer.link.store", "Store"), link: "/london-store" },
      { name: t("footer.link.book_an_appointment", "Book an Appointment"), link: "/bespoke/" },
      { name: t("footer.link.find_us", "Find Us"), link: "/faq/" },
      { name: t("footer.link.lab_grown_diamonds", "Lab Grown Diamonds"), link: "/learn/lab-grown-diamonds/" },
    ],
    [
      { name: t("footer.link.engagement_101", "Engagement 101"), link: "/learn/diamond-buying-guide" },
      { name: t("footer.tc", "Terms & Conditions"), link: "/terms-and-conditions/" },
    ],
  ];

  return (
    <DesktopFooterContainer>
      <FooterContainer>
        <FooterContent>
          <LogoMenusContainer>
            <Logo>Kimaï</Logo>
            <Menus>
              {menuList.map((list, index) => (
                <Column key={index}>
                  {list.map((item, idx) => (
                    <Link to={item.link} key={idx}>{item.name}</Link>
                  ))}
                </Column>
              ))}
            </Menus>
          </LogoMenusContainer>
          <SignupContainer>
            <SignupHeading>{t("sign up to our newsletter", "Sign up to our newsletter")}</SignupHeading>
            <SignupInputContainer>
              <div>
                <Label>Your email address</Label>
                <SignupInput type="email" placeholder="example@gmail.com" />
              </div>
              <div>
                <Label>Your birthday</Label>
                <BirthdayInput type="text" placeholder="day" />
                <BirthdaySelect>
                  <option>month</option>
                </BirthdaySelect>
              </div>
            </SignupInputContainer>
            <SubscribeButton>subscribe</SubscribeButton>
          </SignupContainer>
        </FooterContent>
        <LocationContainer>
          <LocationContent>
            <div>
              {t("header.shipping_to", "Shipping to")} United Kingdom (GBP £)
            </div>
            <SocialLinks>
              {socialLinks.map((social, index) => (
                <a href={social.link} key={index} target="_blank" rel="noopener noreferrer">
                  {social.name}
                </a>
              ))}
            </SocialLinks>
            <CertifiedImage src={BcorpCertified} alt="Certified B Corporation" />
          </LocationContent>
        </LocationContainer>
      </FooterContainer>
    </DesktopFooterContainer>
  );
};

export default withTranslation()(BFCMFooter);
